import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Pencil, AlertCircle, CheckCircle2, RefreshCw, X, Upload } from "lucide-react";
import { formatApiError } from "../AuthContext";
import { useLang } from "../LanguageContext";
import ConfirmDialog from "./ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", excerpt: "", content: "", image_url: "" };

export default function AdminNewsManager() {
  const { t, lang } = useLang();

  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null); // {id, title}

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/news`);
      setItems(data);
    } catch {
      setError(t("admin_news.load_error"));
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setSuccess("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads/image`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image_url: data.url }));
      setSuccess(t("admin_news.upload_success"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setForm({
      title: n.title,
      excerpt: n.excerpt,
      content: n.content,
      image_url: n.image_url || "",
    });
    setError("");
    setSuccess("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content.trim(),
      image_url: form.image_url.trim() || null,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/news/${editingId}`, payload, { withCredentials: true });
        setSuccess(t("admin_news.success_update"));
        setEditingId(null);
      } else {
        await axios.post(`${API}/news`, payload, { withCredentials: true });
        setSuccess(t("admin_news.success_create"));
      }
      setForm(EMPTY);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setError("");
    setSuccess("");
    try {
      await axios.delete(`${API}/news/${pendingDelete.id}`, { withCredentials: true });
      if (editingId === pendingDelete.id) cancelEdit();
      setSuccess(t("admin_news.success_delete"));
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  return (
    <section className="mt-14" data-testid="admin-news-manager">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow mb-1">{t("admin_news.eyebrow")}</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-ink">
            {t("admin_news.title")}
          </h2>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-forest"
          data-testid="admin-news-refresh"
        >
          <RefreshCw size={15} /> {t("admin_news.refresh")}
        </button>
      </div>

      {/* Create / Edit form */}
      <form
        onSubmit={onSubmit}
        className="card-soft p-6 sm:p-8 space-y-4"
        data-testid="admin-news-form"
      >
        <h3 className="font-heading text-lg font-semibold text-ink flex items-center gap-2">
          {editingId ? <Pencil size={18} className="text-forest" /> : <Plus size={18} className="text-forest" />}
          {editingId ? t("admin_news.edit_title") : t("admin_news.form_title")}
        </h3>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">
              {t("admin_news.label_title")}
            </label>
            <input
              name="title"
              required
              minLength={3}
              value={form.title}
              onChange={onChange}
              className="field"
              placeholder={t("admin_news.placeholder_title")}
              data-testid="admin-news-input-title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">
              {t("admin_news.label_image")}
            </label>
            <div className="flex items-center gap-3">
              <input
                name="image_url"
                type="url"
                value={form.image_url}
                onChange={onChange}
                className="field flex-1"
                placeholder="https://…"
                data-testid="admin-news-input-image"
              />
              <label
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm border border-forest-light text-forest-deep shrink-0 ${uploading ? "opacity-60" : "cursor-pointer hover:bg-forest-light/40"}`}
                data-testid="admin-news-upload-label"
              >
                {uploading ? <RefreshCw size={15} className="animate-spin" /> : <Upload size={15} />}
                {uploading ? t("admin_news.uploading") : t("admin_news.upload")}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  disabled={uploading}
                  onChange={onFileSelect}
                  data-testid="admin-news-upload-input"
                />
              </label>
            </div>
            <p className="text-xs text-ink-muted mt-1.5">{t("admin_news.upload_hint")}</p>
            {form.image_url && (
              <div className="mt-3 flex items-center gap-3" data-testid="admin-news-image-preview">
                <img src={form.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                  className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-red-700"
                  data-testid="admin-news-image-remove"
                >
                  <X size={14} /> {t("admin_news.remove_image")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">
            {t("admin_news.label_excerpt")}
          </label>
          <input
            name="excerpt"
            required
            minLength={5}
            value={form.excerpt}
            onChange={onChange}
            className="field"
            placeholder={t("admin_news.placeholder_excerpt")}
            data-testid="admin-news-input-excerpt"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">
            {t("admin_news.label_content")}
          </label>
          <textarea
            name="content"
            required
            minLength={10}
            rows={5}
            value={form.content}
            onChange={onChange}
            className="field resize-y"
            placeholder={t("admin_news.placeholder_content")}
            data-testid="admin-news-input-content"
          />
        </div>

        {error && (
          <div
            className="rounded-2xl bg-red-50 border border-red-200 text-red-800 px-4 py-2.5 text-sm flex items-start gap-2"
            data-testid="admin-news-error"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div
            className="rounded-2xl bg-forest-light/60 border border-forest-light text-forest-deep px-4 py-2.5 text-sm flex items-center gap-2"
            data-testid="admin-news-success"
          >
            <CheckCircle2 size={16} className="text-forest" />
            <span>{success}</span>
          </div>
        )}

        <div className="flex justify-end gap-3">
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm border border-cream-300 text-ink-muted hover:bg-cream-100"
              data-testid="admin-news-cancel-edit"
            >
              <X size={15} />{t("admin_news.cancel")}
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary disabled:opacity-60"
            data-testid="admin-news-submit"
          >
            {editingId
              ? (submitting ? t("admin_news.saving") : t("admin_news.save"))
              : (submitting ? t("admin_news.submitting") : t("admin_news.submit"))}
            {editingId ? <Pencil size={16} /> : <Plus size={16} />}
          </button>
        </div>
      </form>

      {/* List */}
      <div className="mt-8">
        <h3 className="font-heading text-lg font-semibold text-ink mb-4">
          {t("admin_news.list_title")} {items ? `(${items.length})` : ""}
        </h3>

        {!items ? (
          <div className="text-sm text-ink-muted">{t("admin_news.list_loading")}</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-ink-muted">{t("admin_news.list_empty")}</div>
        ) : (
          <ul className="space-y-3">
            {items.map((n, i) => (
              <li
                key={n.id}
                className="card-soft p-5 flex items-start gap-4"
                data-testid={`admin-news-item-${i}`}
              >
                {n.image_url ? (
                  <img
                    src={n.image_url}
                    alt=""
                    className="w-20 h-20 rounded-2xl object-cover shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-cream-200 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-ink-muted mb-1">
                    {new Date(n.published_at).toLocaleDateString(
                      lang === "fr" ? "fr-FR" : "en-GB"
                    )}
                  </div>
                  <div className="font-heading text-base font-semibold text-ink truncate">
                    {n.title}
                  </div>
                  <div className="text-sm text-ink-muted line-clamp-2">{n.excerpt}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(n)}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm border border-forest-light text-forest-deep hover:bg-forest-light/40"
                    data-testid={`admin-news-edit-${i}`}
                    aria-label={t("admin_news.edit")}
                  >
                    <Pencil size={14} /> {t("admin_news.edit")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDelete({ id: n.id, title: n.title })}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm border border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`admin-news-delete-${i}`}
                    aria-label={t("admin_news.delete")}
                  >
                    <Trash2 size={14} /> {t("admin_news.delete")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={t("admin_news.confirm_title")}
        description={
          pendingDelete
            ? `« ${pendingDelete.title} » — ${t("admin_news.confirm_desc")}`
            : t("admin_news.confirm_desc")
        }
        confirmLabel={t("admin_news.confirm_yes")}
        cancelLabel={t("admin_news.confirm_no")}
        tone="danger"
        onConfirm={confirmDelete}
        testIdPrefix="admin-news-confirm"
      />
    </section>
  );
}
